import { useMemo } from 'react';
import { useLocalStorage } from 'react-use';
import {
  isSuitableIntegration,
  selectGoogleIntegrations,
} from '@/features/interactions/addToGoogle/calendar.lib';
import {
  isSuitableIntegration as isSuitableOutlookIntegration,
  selectOutlookIntegrations,
} from '@/features/interactions/addToOutlook/calendar.lib';
import { useIntegrations } from '@/pages/Schedule/hooks/useIntegrations';
import { PlatformTypes } from '@/types/gql.generated';
import type { Destination } from '../types';

type Ret = {
  destination: Destination | null;
  setDestination: (destination: Destination | null) => void;
};

// `platform` scopes the destination to a single provider's drawer. The magic
// export form is shared between Google and Outlook and reads one shared "last
// destination" (localStorage), so without scoping the Outlook drawer would
// inherit — and show the export status of — a Google destination and vice
// versa. Pass a platform to only honor a saved destination of that provider and
// to gate the Google auto-default. Omit it for unscoped callers (unchanged
// behavior: accept any suitable saved destination, auto-default to Google).
export const useCurrentDestination = (platform?: PlatformTypes): Ret => {
  const { data: integrations = [] } = useIntegrations();
  const [savedDestination = null, setDestination] =
    useLocalStorage<Destination | null>('_ah_last_magic_destination', null);
  const googleIntegrations = selectGoogleIntegrations(integrations).filter(
    isSuitableIntegration
  );
  // A saved destination may belong to either provider — the export form is
  // shared and the picker supplies the platform — so validate against both.
  const suitableIntegrations = [
    ...googleIntegrations,
    ...selectOutlookIntegrations(integrations).filter(
      isSuitableOutlookIntegration
    ),
  ];
  const matchesPlatform = !platform || savedDestination?.platform === platform;
  const isSavedDestinationSuitable =
    matchesPlatform &&
    suitableIntegrations.some((item) => {
      if (item.id !== savedDestination?.integration.id) {
        return false;
      }
      return item.calendars.some((calendar) => {
        return (
          !calendar.readonly && calendar.uri === savedDestination.calendar.uri
        );
      });
    });

  // Auto-default only to a Google primary calendar (unchanged behavior), and
  // only for the Google form / unscoped callers. The Outlook drawer has no
  // auto-default — the user explicitly picks an Outlook calendar. This is the
  // asymmetry the Google destination module declares as integration.isAutoDefault.
  const allowGoogleDefault =
    !platform || platform === PlatformTypes.GoogleIntegration;
  const primaryIntegration = googleIntegrations[0];
  const primaryCalendar = primaryIntegration?.calendars.find((calendar) => {
    return calendar.id === primaryIntegration.externalId;
  });

  const destination = useMemo((): Destination | null => {
    if (isSavedDestinationSuitable && savedDestination) {
      return savedDestination;
    }
    if (allowGoogleDefault && primaryIntegration && primaryCalendar) {
      return {
        platform: PlatformTypes.GoogleIntegration,
        integration: primaryIntegration,
        calendar: primaryCalendar,
      };
    }
    return null;
  }, [
    isSavedDestinationSuitable,
    savedDestination,
    allowGoogleDefault,
    primaryIntegration,
    primaryCalendar,
  ]);

  return { destination, setDestination };
};
